<template>
  <div class="w-full max-w-2xl" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="p-6 border border-emerald-200 dark:border-emerald-800/60 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 shadow-sm">
      <div class="flex items-start" :class="isRtl === true ? 'flex-row-reverse' : 'flex-row'">
        <div class="w-14 h-14 rounded-2xl bg-stone-800 dark:bg-stone-200 flex items-center justify-center text-white dark:text-stone-900 text-xl font-bold flex-shrink-0" :class="isRtl === true ? 'ml-4' : 'mr-4'">
          {{ agentInitial }}
        </div>
        <div class="flex-1 min-w-0" :style="isRtl === true ? { textAlign: 'right' } : { textAlign: 'left' }">
          <div class="text-lg font-semibold text-emerald-900 dark:text-emerald-100">{{ preview.agentName }}</div>
          <div class="text-sm text-emerald-700 dark:text-emerald-300 mt-1">{{ preview.typeLabel }}</div>
          <div class="text-sm text-gray-700 dark:text-gray-300 mt-3 leading-6 line-clamp-3">{{ preview.jobDescription }}</div>
          <div class="mt-3 flex flex-wrap -m-1">
            <span
              v-for="channel in preview.channels"
              :key="channel"
              class="text-xs font-medium px-2 py-1 rounded-full border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 m-1"
            >
              {{ channel }}
            </span>
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 mt-3">{{ preview.knowledgeLabel }}</div>
        </div>
      </div>

      <div class="mt-6 pt-5 border-t border-emerald-200 dark:border-emerald-800/60">
        <div class="flex flex-wrap justify-end -m-1">
          <button
            v-if="hasWebChannel === true"
            type="button"
            class="btn m-1"
            @click="openChat"
          >
            <Icon icon="tabler:message-circle" width="18" height="18" :class="isRtl === true ? 'ml-2' : 'mr-2'" />
            {{ t('aiChatAgentSetup.buttons.openChat', 'Open Chat') }}
          </button>
          <button
            v-if="hasPhoneChannel === true"
            type="button"
            class="btn m-1"
            @click="openCall"
          >
            <Icon icon="tabler:phone" width="18" height="18" :class="isRtl === true ? 'ml-2' : 'mr-2'" />
            {{ t('agentCreate.preview.call', 'Call') }}
          </button>
        </div>
        <button type="button" class="btn w-full mt-4 h-12 text-base" @click="showEditorChoice = true">
          <Icon icon="tabler:settings" width="20" height="20" :class="isRtl === true ? 'ml-2' : 'mr-2'" />
          {{ t('agentCreate.preview.editAgent', 'Edit full agent setup') }}
        </button>
      </div>
    </div>
    <dialog :open="showEditorChoice === true" class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg">{{ t('agentCreate.preview.editChoiceTitle', 'How would you like to continue?') }}</h3>
        <p class="py-3 text-sm text-slate-600 dark:text-slate-400">{{ t('agentCreate.preview.editChoiceDescription', 'You can switch between these views later from the selector at the top-left of setup.') }}</p>
        <div class="rounded-xl border border-base-300 p-4">
          <div class="font-medium">{{ t('agentCreate.preview.chatFineTuneTitle', 'Fine tune with chat') }}</div>
          <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">{{ t('agentCreate.preview.chatFineTuneDescription', 'Use the guided, conversation-focused editor.') }}</p>
          <button type="button" class="btn btn-sm mt-3" @click="openEditor('light')">{{ t('agentCreate.preview.openChatFineTune', 'Open chat fine tuning') }}</button>
        </div>
        <div class="rounded-xl border border-base-300 p-4 mt-3">
          <div class="font-medium">{{ t('agentCreate.preview.uiFineTuneTitle', 'Fine tune with the full UI') }}</div>
          <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">{{ t('agentCreate.preview.uiFineTuneDescription', 'Configure knowledge, rules, tools, channels, and advanced settings.') }}</p>
          <button type="button" class="btn btn-sm mt-3" @click="openEditor('full')">{{ t('agentCreate.preview.openUiFineTune', 'Open full setup') }}</button>
        </div>
        <div class="modal-action">
          <button type="button" class="btn" @click="showEditorChoice = false">{{ t('common.cancel', 'Cancel') }}</button>
        </div>
      </div>
    </dialog>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import { useToast } from 'vue-toastification';
import { normalizeOpenAgentTelValue } from '@/shared/utils/openAgentChannels.js';
import { resolveOpenWebAgentTarget } from '@/shared/utils/openAgentTargets.js';

const props = defineProps({
  preview: {
    type: Object,
    required: true
  }
});

const router = useRouter();
const toast = useToast();
const { t, currentLanguage, availableLanguages } = useI18n();
const showEditorChoice = ref(false);

const isRtl = computed(() => {
  const language = availableLanguages.value.find((item) => item.code === currentLanguage.value);
  if (language === undefined || language === null) {
    return false;
  }
  return language.dir === 'rtl';
});

const agentInitial = computed(() => {
  if (typeof props.preview.agentName === 'string' && props.preview.agentName.length > 0) {
    return props.preview.agentName.charAt(0).toUpperCase();
  }
  return 'A';
});

const hasWebChannel = computed(() => {
  return Array.isArray(props.preview.channels) === true && (props.preview.channels.includes('Web') === true || props.preview.channels.includes('Web Voice Agent') === true);
});

const hasPhoneChannel = computed(() => {
  return Array.isArray(props.preview.channels) === true && props.preview.channels.includes('Phone') === true && typeof props.preview.phoneNumber === 'string' && props.preview.phoneNumber.length > 0;
});

function openChat() {
  if (typeof props.preview.agentId === 'string' && props.preview.agentId.length > 0) {
    const openTarget = resolveOpenWebAgentTarget({
      agentid: props.preview.agentId,
      agenttype: 'PublicAgent'
    });
    if (openTarget !== null && openTarget.mode === 'router') {
      router.push(openTarget.url);
      return;
    }
    if (openTarget !== null && openTarget.mode === 'window') {
      window.open(openTarget.url, '_blank');
      return;
    }
    router.push(`/agent/${props.preview.agentId}`);
    return;
  }
  toast.info(t('agentCreate.preview.chatPending', 'Chat will be available once the agent is saved.'));
}

function openCall() {
  const telValue = normalizeOpenAgentTelValue(props.preview.phoneNumber);
  if (telValue.length === 0) {
    toast.error(t('agentCreate.preview.noPhone', 'No phone number is connected yet.'));
    return;
  }
  window.open(`tel:${telValue}`, '_self');
}

function openEditor(setupType) {
  if (typeof props.preview.agentId === 'string' && props.preview.agentId.length > 0) {
    const selectedSetupType = setupType === 'light' ? 'light' : 'full';
    const path = selectedSetupType === 'light' ? '/aiChatAgentSetupLight' : '/aiChatAgentSetup';
    router.push({ path, query: { edit: props.preview.agentId, setupType: selectedSetupType } });
    return;
  }
  router.push({ path: '/aiChatAgentSetup' });
}
</script>
